import React from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { History, Location } from 'history'

import RootState from '../../core/root-state'
import { adminUsersActions } from '../../core/admin-users/actions'
import { AdminUserDetail } from '../../core/admin-users/admin-users'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import QueryEmail from '../components/users/query-email'
import Button from '../components/button'
import { roleType, Permission } from '../../core/auth0'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import AdminUserInfoTable from '../components/admin-users/admin-user-info-table'
import styles from '../styles/modules/admin-users/admin-users.module.scss'

interface Props {
  role: roleType
  isLoading: boolean
  isUpdating: boolean
  searchResult: AdminUserDetail | null
  error: Error | null
  location: Location
  history: History
  searchAdminUserRequest: (email: string) => void
  updateAdminUserRoleRequest: (request: {
    email: string
    roleName: string
  }) => void
  resetAdminUsers: () => void
}

interface State {
  readonly email: string
  readonly didSearchFinish: boolean
  readonly isEditing: boolean
  readonly selectedRoleName: string
}

const strings = {
  title: '管理ユーザー',
  queryButton: '検索',
}

class AdminUsersScreen extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      email: '',
      didSearchFinish: false,
      isEditing: false,
      selectedRoleName: '',
    }
  }

  componentDidMount() {
    this.props.resetAdminUsers()
  }

  handleOnChangeEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ email: event.target.value })
  }

  handleOnClickQueryEmail = () => {
    if (!this.state.email.trim()) {
      alert('メールアドレスを入力してください')
      return
    }

    this.props.searchAdminUserRequest(this.state.email)
    this.setState({ didSearchFinish: true, isEditing: false })
  }

  handleOnEditClick = () => {
    this.setState({
      isEditing: true,
      selectedRoleName: this.props.searchResult?.role || '',
    })
  }

  handleOnRoleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ selectedRoleName: event.target.value })
  }

  handleOnSave = () => {
    if (!this.props.searchResult) {
      alert('先にユーザーを検索してください')
      return
    }

    if (!this.state.selectedRoleName) {
      alert('変更するロールを選択してください')
      return
    }

    this.props.updateAdminUserRoleRequest({
      email: this.props.searchResult.email,
      roleName: this.state.selectedRoleName,
    })

    this.setState({ isEditing: false })
  }

  handleOnCancel = () => {
    this.setState({
      isEditing: false,
      selectedRoleName: '',
    })
  }

  render() {
    const { isLoading, searchResult } = this.props
    const { email, didSearchFinish } = this.state

    return (
      <Layout location={'admin_users'}>
        <Wrapper title={strings.title}>
          <div className={styles.queryContainer}>
            <QueryEmail
              email={email}
              onChange={event => this.handleOnChangeEmail(event)}
            />
            <Button
              title={strings.queryButton}
              onClick={this.handleOnClickQueryEmail}
              className={styles.queryButton}
              isLoading={isLoading}
            />
          </div>

          {/* ユーザー情報 */}
          {searchResult && (
            <AdminUserInfoTable
              adminUser={searchResult}
              isEditing={this.state.isEditing}
              selectedRoleName={this.state.selectedRoleName}
              onEditClick={this.handleOnEditClick}
              onRoleChange={this.handleOnRoleChange}
              onSave={this.handleOnSave}
              onCancel={this.handleOnCancel}
            />
          )}

          {/* ユーザーが見つからなかった時の表示 */}
          {!searchResult && !isLoading && didSearchFinish && (
            <div className={styles.textContainer}>
              <p>ユーザーが見つかりません</p>
            </div>
          )}
        </Wrapper>
      </Layout>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  role: state.auth0.role,
  isLoading: state.adminUsers.isLoading,
  isUpdating: state.adminUsers.isUpdating,
  searchResult: state.adminUsers.searchResult,
  error: state.adminUsers.error,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  searchAdminUserRequest: (email: string) =>
    dispatch(adminUsersActions.searchAdminUserRequest(email)),
  updateAdminUserRoleRequest: (request: { email: string; roleName: string }) =>
    dispatch(adminUsersActions.updateAdminUserRoleRequest(request)),
  resetAdminUsers: () => dispatch(adminUsersActions.resetAdminUsers()),
})

const ConnectedAdminUsersScreen = connect(
  mapStateToProps,
  mapDispatchToProps
)(AdminUsersScreen)

export default withPermissionCheck(ConnectedAdminUsersScreen, {
  requiredPermission: Permission.ADMIN_USER_READ,
  redirectTo: '/404',
})
